import { Injectable, NgZone } from '@angular/core';
import { environment } from 'src/environments/environment.prod';
import { FaceRoiStatus, GazeWebcamService } from '../nav-driver/gaze-webcam/gaze-webcam.service';
import { WebcamService } from '../nav-driver/webcam.service';
import { NavController, AlertController } from '@ionic/angular'; 
import { Router } from '@angular/router';
import { LanguageService } from '../language/language.service';
import { Preferences } from '@capacitor/preferences';

interface FaceCoordinates {
  x: number;
  y: number;
  w: number;
}

type LinkFlowState = {
  visitedLanding: boolean;
  onboardingCompleted: boolean;
  calibrated: boolean;
  stateVersion: number;
};

@Injectable({
  providedIn: 'root'
})
export class LinkService {

  public status: 'idle' | 'starting' | 'ready' | 'error' = 'idle';
  public faceStatus: 'idle' | 'warn' | 'ready' | 'loading' = 'idle';

  public environmentName = environment.environmentName;
  public version = environment.version;

  private roiTimer: ReturnType<typeof setInterval> | null = null;
  private roiRequestInFlight = false;

  private calibrationBaseline: FaceCoordinates | null = null;
  private monitorTimer: ReturnType<typeof setInterval> | null = null;
  private isAlertShowing = false;
  
  private readonly THRESHOLD_XY = 0.15; 
  private readonly THRESHOLD_Z = 0.25; 

  private readonly ONBOARDING_KEY = 'conversia_onboarding_complete';
  private readonly DWELL_TIME_KEY = 'conversia_dwell_time';

  private readonly FLOW_KEY = 'conversia_link_flow_v1';
  private readonly FLOW_VERSION = 1;

  private readonly LEGACY_VISITED = 'conversia_visited_landing';
  private readonly LEGACY_ONBOARDING = 'conversia_completed_onboarding';
  private readonly LEGACY_CALIBRATED = 'conversia_is_calibrated';


  constructor(
    private gaze: GazeWebcamService,
    public webcam: WebcamService,
    public navCtrl: NavController,
    private router: Router,
    private zone: NgZone,
    public lang: LanguageService,
    private alertCtrl: AlertController 
  ) { }

  async startCalibration() {
    if (this.status === 'starting') return;
    this.status = 'starting';
    this.stopFaceAlignmentMonitoring();
    this.stopPositionMonitoring();
    await this.webcam.stop().catch(() => {});

    try {
      const el = document.documentElement as any;
      if (el.requestFullscreen) await el.requestFullscreen();
    } catch (e) { }

    this.zone.run(() => {
      this.router.navigate(['/link/gaze-calibration']).then(success => {
        if (!success) this.router.navigate(['/gaze-calibration']);
      });
    });

    setTimeout(() => { this.status = 'idle'; }, 3000);
  }

  async getFlow(): Promise<LinkFlowState> {
    const { value } = await Preferences.get({ key: this.FLOW_KEY });
    if (value) {
      try { return JSON.parse(value) as LinkFlowState; }
      catch { }
    }

    const legacyVisited = localStorage.getItem(this.LEGACY_VISITED) === 'true';
    const legacyOnboarding = localStorage.getItem(this.LEGACY_ONBOARDING) === 'true';
    const legacyCalibrated = localStorage.getItem(this.LEGACY_CALIBRATED) === 'true';

    const prefOnboarding = (await Preferences.get({ key: this.ONBOARDING_KEY })).value === 'true';

    const migrated: LinkFlowState = {
      visitedLanding: legacyVisited,
      onboardingCompleted: legacyOnboarding || prefOnboarding,
      calibrated: legacyCalibrated,
      stateVersion: this.FLOW_VERSION
    };

    await Preferences.set({ key: this.FLOW_KEY, value: JSON.stringify(migrated) });

    localStorage.removeItem(this.LEGACY_VISITED);
    localStorage.removeItem(this.LEGACY_ONBOARDING);
    localStorage.removeItem(this.LEGACY_CALIBRATED);

    await Preferences.set({ key: this.ONBOARDING_KEY, value: migrated.onboardingCompleted ? 'true' : 'false' });

    return migrated;
  }

  async patchFlow(patch: Partial<LinkFlowState>): Promise<void> {
    const current = await this.getFlow();
    const next: LinkFlowState = { ...current, ...patch, stateVersion: this.FLOW_VERSION };
    await Preferences.set({ key: this.FLOW_KEY, value: JSON.stringify(next) });

    if (patch.onboardingCompleted !== undefined) {
      await Preferences.set({ key: this.ONBOARDING_KEY, value: patch.onboardingCompleted ? 'true' : 'false' });
    }
  }

  async resetFlow(): Promise<void> {
    await Preferences.remove({ key: this.FLOW_KEY });
    await Preferences.remove({ key: this.ONBOARDING_KEY });

    localStorage.removeItem(this.LEGACY_VISITED);
    localStorage.removeItem(this.LEGACY_ONBOARDING);
    localStorage.removeItem(this.LEGACY_CALIBRATED);
  }

  async getState(): Promise<LinkFlowState> {
    return this.getFlow();
  }

  async resetState(): Promise<void> {
    return this.resetFlow();
  }

  async setState(patch: Partial<LinkFlowState>): Promise<void> {
    return this.patchFlow(patch);
  }

  async resetAllConfigurationsForWebcamSetup() {
    try {
      this.stopPositionMonitoring(); 
      await this.gaze.stop().catch(() => { });
      await this.gaze.restartGazeConfiguration();
      await this.webcam.resetConfiguration();
      this.faceStatus = 'loading';
      this.webcam.hint$.next(null);
    } catch (error) {
      console.error('[WebcamSetup] Error resetting configurations:', error);
    }
  }

  async tickFaceAlignment(): Promise<void> {
    if (this.roiRequestInFlight) return;
    this.roiRequestInFlight = true;

    const safetyUnlock = setTimeout(() => {
      if (this.roiRequestInFlight) {
        this.roiRequestInFlight = false;
      }
    }, 2000);

    try {
      const status = await this.gaze.requestRoiStatus(); 
      this.zone.run(() => this.updateFaceStatus(status)); 
    } catch (err) {
    } finally {
      clearTimeout(safetyUnlock);
      this.roiRequestInFlight = false;
    }
  }

  updateFaceStatus(status: FaceRoiStatus | null): void {
    const next = this.evaluateFaceStatus(status); 
    if (this.faceStatus === 'loading' && next === 'idle') return;
    if (next !== this.faceStatus) this.faceStatus = next;
    
    if (next === 'warn') {
      this.webcam.hint$.next(this.selectAlignmentHint(status));
    } else {
      this.webcam.hint$.next(null);
    }
  }

  evaluateFaceStatus(status: FaceRoiStatus | null): 'idle' | 'warn' | 'ready' {
    if (!status || !status.hasFace || !status.frame || status.frame.w <= 0) return 'idle';
    const { roi, frame } = status;
    if (!roi || roi.w <= 0 || roi.h <= 0) return 'idle';

    const faceCenterX = (roi.x + roi.w / 2) / frame.w;
    const faceCenterY = (roi.y + roi.h / 2) / frame.h;

    const offsetX = Math.abs(faceCenterX - 0.5);
    const offsetY = Math.abs(faceCenterY - 0.5);

    const sizeRatio = roi.w / frame.w;

    const centered = offsetX <= 0.08 && offsetY <= 0.10;
    const goodSize = sizeRatio >= 0.18 && sizeRatio <= 0.65;

    return centered && goodSize ? 'ready' : 'warn';
  }

  selectAlignmentHint(status: FaceRoiStatus | null): string {
    if (!status || !status.frame || !status.roi) return 'Centralize o rosto no círculo';
    const { roi, frame } = status;
    if (frame.w <= 0 || roi.w <= 0) return 'Centralize o rosto no círculo';

    const sizeRatio = roi.w / frame.w;
    if (sizeRatio < 0.18) return 'Aproxime-se um pouco da câmera';
    if (sizeRatio > 0.65) return 'Afaste-se um pouco da câmera';

    const faceCenterX = (roi.x + roi.w / 2) / frame.w;
    const faceCenterY = (roi.y + roi.h / 2) / frame.h;
    const offsetX = faceCenterX - 0.5;
    const offsetY = faceCenterY - 0.5;

    if (Math.abs(offsetX) > Math.abs(offsetY)) {
      if (offsetX > 0.08) return 'Mova um pouco para a esquerda';
      if (offsetX < -0.08) return 'Mova um pouco para a direita';
    } else {
      if (offsetY > 0.10) return 'Mova um pouco para cima';
      if (offsetY < -0.10) return 'Mova um pouco para baixo';
    }

    return 'Centralize o rosto no círculo';
  }

  async hasCompletedOnboarding(): Promise<boolean> {
    const flow = await this.getFlow();
    return flow.onboardingCompleted;
  }

  async setOnboardingComplete(): Promise<void> {
    await this.patchFlow({ onboardingCompleted: true });  
  }

  async setVisitedLanding(): Promise<void> {
    await this.patchFlow({ visitedLanding: true });
  }

  async getOnboardingStatus(): Promise<boolean> {
    const flow = await this.getFlow();
    return flow.onboardingCompleted;
  }

  async setDwellTime(ms: number): Promise<void> {
    await Preferences.set({ key: this.DWELL_TIME_KEY, value: ms.toString() });
  }
  
  async getDwellTime(): Promise<number> {
    const { value } = await Preferences.get({ key: this.DWELL_TIME_KEY });
    return value ? parseInt(value, 10) : 1500;
  }

  async setCalibrated(): Promise<void> {
    await this.patchFlow({ calibrated: true });
  }

  async saveCalibrationBaseline() {
    try {
      const status = await this.gaze.requestRoiStatus();
      if (status && status.roi && status.frame) {
        this.calibrationBaseline = {
          x: status.roi.x / status.frame.w,
          y: status.roi.y / status.frame.h,
          w: status.roi.w / status.frame.w 
        };
        this.startPositionMonitoring();
      }
    } catch (err) { }
  }

  startPositionMonitoring() {
    this.stopPositionMonitoring(); 
    this.monitorTimer = setInterval(async () => {
      if (this.isAlertShowing || !this.calibrationBaseline) return;
      try {
        const status = await this.gaze.requestRoiStatus();
        this.checkPositionDeviation(status);
      } catch (e) { }
    }, 2500);
  }

  stopPositionMonitoring() {
    if (this.monitorTimer) {
      clearInterval(this.monitorTimer);
      this.monitorTimer = null;
    }
  }

  private checkPositionDeviation(status: FaceRoiStatus) {
    if (!status || !status.roi || !status.frame || !this.calibrationBaseline) return;
    const current = {
      x: status.roi.x / status.frame.w,
      y: status.roi.y / status.frame.h,
      w: status.roi.w / status.frame.w
    };
    const dX = Math.abs(current.x - this.calibrationBaseline.x);
    const dY = Math.abs(current.y - this.calibrationBaseline.y);
    const dW = Math.abs(current.w - this.calibrationBaseline.w);
    if (dX > this.THRESHOLD_XY || dY > this.THRESHOLD_XY || dW > this.THRESHOLD_Z) {
      this.triggerRecalibrationAlert();
    }
  }

  private async triggerRecalibrationAlert() {
    if (this.isAlertShowing) return;
    this.isAlertShowing = true;

    const alert = await this.alertCtrl.create({
      header: '⚠️ Movimento Detectado',
      message: 'Você se moveu muito. Para garantir a precisão, precisamos recalibrar.',
      backdropDismiss: false, 
      buttons: [
        { 
          text: 'Ignorar', 
          role: 'cancel', 
          handler: () => { 
            setTimeout(() => { this.isAlertShowing = false; }, 5000); 
          } 
        },
        { 
          text: 'Recalibrar', 
          handler: () => { 
            this.isAlertShowing = false;
            this.stopPositionMonitoring();
            this.zone.run(() => {
              this.router.navigateByUrl('/link/webcam-setup'); 
            });
          } 
        }
      ]
    });
    await alert.present();
  }

  beginFaceAlignmentMonitoring(): void {
    if (this.roiTimer !== null) return;
    this.tickFaceAlignment();
    this.roiTimer = setInterval(() => this.tickFaceAlignment(), 500);
  }

  stopFaceAlignmentMonitoring(): void {
    if (this.roiTimer !== null) {
      clearInterval(this.roiTimer);
      this.roiTimer = null;
    }
    this.roiRequestInFlight = false;
  }

  get circleStateClass(): string {
    return `state-${this.faceStatus}`;
  }
  
}